import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Paper or digital: how a mock exam is sat.
 *
 * Paper (the printed-booklet view with its separate answer sheet) is the
 * default wherever a paper version exists; the digital simulator covers the
 * rest. The choice is remembered per browser and, for signed-in users, on
 * their profile (users/{uid}.preferences.examMode).
 */

/** Paper mode is released (2026-10-06): every exam below has its paper version. */
export const PAPER_MODE_ENABLED = true;

/** Exams with a paper version (provider-level slugs). */
const PAPER_SLUGS = new Set(['telc-b1', 'telc-b2', 'telc-c1', 'goethe-b1', 'goethe-b2', 'goethe-c1', 'osd-b1', 'osd-b2', 'osd-c1']);

export const paperSlugFor = (provider, level) =>
  `${String(provider || '').toLowerCase().replace(/ö/g, 'o')}-${String(level || '').toLowerCase()}`;

/** Whether this exam (provider + level) can be sat on paper. */
export const supportsPaper = (provider, level) =>
  PAPER_MODE_ENABLED && PAPER_SLUGS.has(paperSlugFor(provider, level));

export const examSupportsPaper = (examData) =>
  Boolean(examData) && supportsPaper(examData.examProvider, examData.level);

const KEY = 'logami.examMode';
export const DEFAULT_EXAM_MODE = 'paper';

/** The remembered mode ('paper' | 'digital'); paper when nothing is stored. */
export const getExamModePreference = () => {
  try {
    const stored = window.localStorage.getItem(KEY);
    return stored === 'digital' || stored === 'paper' ? stored : DEFAULT_EXAM_MODE;
  } catch {
    return DEFAULT_EXAM_MODE;
  }
};

/** Remember the mode on this device and, when signed in, on the profile. */
export const setExamModePreference = (mode, uid = null) => {
  if (mode !== 'paper' && mode !== 'digital') return;
  try { window.localStorage.setItem(KEY, mode); } catch { /* storage unavailable */ }
  if (uid) {
    setDoc(doc(db, 'users', uid), { preferences: { examMode: mode } }, { merge: true })
      .catch((err) => console.warn('Could not save the exam mode preference:', err));
  }
};

/**
 * A signed-in user's saved mode, for a device that has none stored yet (e.g.
 * chosen in onboarding on another device). Resolves to null when there is none.
 */
export const loadExamModePreference = async (uid) => {
  if (!uid) return null;
  try {
    const hasLocal = window.localStorage.getItem(KEY);
    if (hasLocal === 'paper' || hasLocal === 'digital') return hasLocal;
  } catch { /* fall through to the profile */ }
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    const mode = snap.data()?.preferences?.examMode;
    if (mode === 'paper' || mode === 'digital') {
      try { window.localStorage.setItem(KEY, mode); } catch { /* storage unavailable */ }
      return mode;
    }
  } catch (err) {
    console.warn('Could not load the exam mode preference:', err);
  }
  return null;
};
